import React, { useMemo, useEffect, useContext, useCallback } from 'react'
import usePrevious from 'hooks/usePrevious'
import { useDispatch } from 'react-redux'
import { useIdRegister } from 'hooks/useIdRegister'
import fetchDocumentListItems from 'routes/Card/sagas/fetchDocumentListItems'

const DEFAULT_CONTEXT: {
  registerDocumentListItem: (documentListItemId: number, documentSourceId: number, url: string) => void;
  unregisterDocumentListItem: (documentListItemId: number, documentSourceId: number, url: string) => void;
} = {
  registerDocumentListItem: () => {},
  unregisterDocumentListItem: () => {},
}

export const DocumentListItemProviderContext = React.createContext(DEFAULT_CONTEXT)

export function DocumentListItemProvider ({
  children
}) {
  const dispatch = useDispatch()

  const [ids, registerId, unregisterId] = useIdRegister<string>(0)
  const prevIds = usePrevious(ids)

  useEffect(() => {
    let addedIds: string[] = []

    if (ids.length) {
      if (prevIds?.length) {
        addedIds = ids.filter(boardId => !prevIds.includes(boardId))
      } else {
        addedIds = ids
      }
    }

    if (addedIds.length) {
      dispatch(fetchDocumentListItems({
        documentListItems: addedIds.map(id => {
          const [documentListItemId, documentSourceId, url] = id.split('-')

          return {
            documentListItemId: Number(documentListItemId),
            documentSourceId: Number(documentSourceId),
            url,
          }
        })
      }))
    }
  }, [ids])

  const registerDocumentListItem = useCallback((documentListItemId: number, documentSourceId: number, url: string) =>
    registerId(`${documentListItemId}-${documentSourceId}-${url}`)
  , [registerId])
  const unregisterDocumentListItem = useCallback((documentListItemId: number, documentSourceId: number, url: string) =>
    unregisterId(`${documentListItemId}-${documentSourceId}-${url}`)
  , [unregisterId])

  const value = useMemo(() => ({
    registerDocumentListItem,
    unregisterDocumentListItem,
  }), [registerDocumentListItem, unregisterDocumentListItem])

  return (
    <DocumentListItemProviderContext.Provider value={value}>
      {children}
    </DocumentListItemProviderContext.Provider>
  )
}

export function useDocumentListItemProvider (documentListItemId: number, documentSourceId: number, url: string) {
  const { registerDocumentListItem, unregisterDocumentListItem } = useContext(DocumentListItemProviderContext)

  useEffect(() => {
    registerDocumentListItem(documentListItemId, documentSourceId, url)

    return () => {
      unregisterDocumentListItem(documentListItemId, documentSourceId, url)
    }
  }, [documentListItemId, documentSourceId, url])
}
